import type { ReactNode } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { errorMessage } from "@/lib/api";

/* ============================================
   Error state — shown when a query fails
   ============================================ */

export function ErrorState({
  error,
  message,
  onRetry,
}: {
  error?: unknown;
  message?: string;
  onRetry?: () => unknown;
}) {
  return (
    <div className="text-center py-16" role="alert">
      <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
        <AlertCircle className="w-5 h-5 text-red-500" aria-hidden />
      </div>
      <p className="text-sm text-[var(--color-ink-muted)] mb-4">{message ?? errorMessage(error)}</p>
      {onRetry && (
        <button
          onClick={() => void onRetry()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-cream-dark)] text-sm font-medium hover:bg-[var(--color-cream)] transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" aria-hidden /> Try again
        </button>
      )}
    </div>
  );
}

/* ============================================
   Empty state
   ============================================ */

export function EmptyState({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="text-center py-16">
      {icon && <div className="flex justify-center mb-4 text-[var(--color-cream-dark)]">{icon}</div>}
      <p className="text-[var(--color-ink-muted)]">{children}</p>
    </div>
  );
}

/* ============================================
   Pagination
   ============================================ */

export function Pagination({
  page,
  total,
  pageSize,
  onChange,
}: {
  page: number;
  total: number;
  pageSize: number;
  onChange: (page: number) => void;
}) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  const button =
    "px-4 py-2 rounded-lg border border-[var(--color-cream-dark)] text-sm font-medium disabled:opacity-30 hover:bg-[var(--color-cream)] transition-colors";
  return (
    <nav className="flex items-center justify-center gap-2 mt-10" aria-label="Pagination">
      <button onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1} className={button}>
        Previous
      </button>
      <span className="text-sm text-[var(--color-ink-muted)] px-3">
        Page {page} of {totalPages}
      </span>
      <button onClick={() => onChange(page + 1)} disabled={page >= totalPages} className={button}>
        Next
      </button>
    </nav>
  );
}
